import { useEffect, useState } from "react";
import { swrJson } from "@/lib/swr";
import { Link } from "react-router-dom";
import { SyncBar } from "@/components/SyncBar";
import { DateRange } from "@/components/DateRange";
import { Drawer } from "../monitoring/articles/detail";
import { Highlight } from "@/components/Highlight";
import { Ch } from "@/components/Fa";
import { NetworkGraph } from "@/components/NetworkGraph";
import { Panel } from "@/components/Panel";
import { Tile } from "@/components/Tile";
import { Gauge } from "@/components/viz/Gauge";
import { ProgressRow } from "@/components/viz/ProgressRow";
import { AreaChart } from "@/components/viz/AreaChart";
import { DayCols, type DayDatum } from "@/components/viz/DayCols";
import { spikeInsight, negShareInsight, topMoverInsight, channelInsight, type Insight } from "@/lib/insights";
import { useTenant } from "@/lib/tenant";

const M = { pos: "var(--mark-pos)", neu: "var(--mark-neu)", neg: "var(--mark-neg)" };
const BLUE = "var(--mark-c1)";
const nf = (x: number) => (Math.abs(x ?? 0) >= 100000
  ? Intl.NumberFormat("id-ID", { notation: "compact", maximumFractionDigits: 1 }).format(x)
  : Intl.NumberFormat("id-ID").format(x ?? 0));
const pct = (a: number, b: number) => (b ? (a / b) * 100 : 0);
const delta = (now: number, prev: number) => (prev > 0 ? ((now - prev) / prev) * 100 : null);
const TONE_ID: Record<string, string> = { positive: "Positif", neutral: "Netral", negative: "Negatif" };
const EWS_TONE: Record<string, { bg: string; fg: string }> = {
  crisis: { bg: "var(--status-danger-bg)", fg: "var(--status-danger)" },
  current: { bg: "var(--status-warn-bg)", fg: "var(--status-warn)" },
  emerging: { bg: "var(--status-warn-bg)", fg: "var(--status-warn)" },
  potential: { bg: "var(--gray-100, #F1F5F9)", fg: "var(--color-text-secondary)" },
};
const wibDay = (d: string) => `${d.slice(8)}/${d.slice(5, 7)}`;
const initials = (s: string) => s.split(" ").slice(0, 2).map((x) => x[0]).join("").toUpperCase();

export default function DashboardPage() {
  const today = new Date(Date.now() + 7 * 3600e3).toISOString().slice(0, 10);
  const defStart = new Date(Date.now() + 7 * 3600e3 - 6 * 86400e3).toISOString().slice(0, 10);
  const [start, setStart] = useState(defStart);
  const [end, setEnd] = useState(today);
  // Panel social hanya digambar bila tenant memang berlangganan modul itu.
  // Menyembunyikan menunya saja tidak cukup: dasbor ini halaman pertama yang
  // dilihat pengguna, dan kartu "Mention social 0" pada tenant media-only
  // membaca sebagai kegagalan sistem, bukan sebagai fitur yang tidak dibeli.
  const { has } = useTenant();
  const adaSocial = has("social_monitoring");
  const adaMedia = has("media_monitoring");
  const adaIssue = has("issue_management");
  const [mode, setMode] = useState<"tren" | "tone">("tren");
  const [d, setD] = useState<any>(null);
  const [sn, setSn] = useState<any>(null);
  const [prevD, setPrevD] = useState<any>(null);
  const [prevSn, setPrevSn] = useState<any>(null);
  const [authors, setAuthors] = useState<any[]>([]);
  const [persons, setPersons] = useState<any[]>([]);
  const [issues, setIssues] = useState<any[]>([]);
  const [net, setNet] = useState<any>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  function load(S = start, E = end) {
    const span = Math.max(1, Math.round((+new Date(E) - +new Date(S)) / 86400e3) + 1);
    const pE = new Date(+new Date(S) - 86400e3).toISOString().slice(0, 10);
    const pS = new Date(+new Date(pE) - (span - 1) * 86400e3).toISOString().slice(0, 10);
    // swrJson: data terakhir langsung tampil dari cache tab ini, versi terbaru menyusul di belakang.
    swrJson(`/api/v1/analytics/overview?start=${S}&end=${E}`, setD);
    swrJson(`/api/v1/analytics/social?start=${S}&end=${E}`, setSn);
    swrJson(`/api/v1/analytics/overview?start=${pS}&end=${pE}`, setPrevD);
    swrJson(`/api/v1/analytics/social?start=${pS}&end=${pE}`, setPrevSn);
    swrJson<any>(`/api/v1/social/authors?start=${S}&end=${E}`, (j) => setAuthors((j?.data ?? []).slice(0, 5)));
    swrJson<any>(`/api/v1/persons/top?start=${S}&end=${E}`, (j) => setPersons((j?.data ?? []).slice(0, 5)));
    swrJson<any>(`/api/v1/social/network?start=${S}&end=${E}`, (j) => setNet(j?.data ?? null));
    swrJson<any>("/api/v1/issues", (j) => setIssues((j?.data ?? []).filter((x: any) => x.status !== "closed").slice(0, 4)));
  }
  useEffect(() => { load(); }, []);

  const qs = `start=${start}&end=${end}`;
  const mt = d?.tones ?? { positive: 0, neutral: 0, negative: 0 };
  const st = sn?.tones ?? { positive: 0, neutral: 0, negative: 0 };
  const pmt = prevD?.tones ?? { positive: 0, neutral: 0, negative: 0 };
  const pst = prevSn?.tones ?? { positive: 0, neutral: 0, negative: 0 };
  const mTot = mt.positive + mt.neutral + mt.negative;
  const sTot = st.positive + st.neutral + st.negative;
  const pmTot = pmt.positive + pmt.neutral + pmt.negative;
  const psTot = pst.positive + pst.neutral + pst.negative;

  const days: DayDatum[] = (() => {
    const map = new Map<string, DayDatum>();
    for (const r of sn?.dailyTone ?? []) {
      const e = map.get(r.day) ?? { day: r.day, positive: 0, neutral: 0, negative: 0 };
      (e as any)[r.tone] = ((e as any)[r.tone] ?? 0) + r.c;
      map.set(r.day, e);
    }
    return [...map.values()].sort((a, b) => a.day.localeCompare(b.day));
  })();
  const totalSeries = days.map((x) => x.positive + x.neutral + x.negative);
  const negSeries = days.map((x) => x.negative);
  const alerts = (d?.ews ?? []).filter((e: any) => e.status !== "normal");
  const cats = (d?.byCategory ?? []) as any[];
  const catSum = cats.reduce((a, c) => a + c.total, 0);
  const chans = (sn?.byChannel ?? []) as any[];
  const chanSum = chans.reduce((a, c) => a + c.total, 0);
  const trending = (d?.trending ?? []) as any[];
  const loading = !d && !sn;

  // Insight yang menunjuk halaman social tidak berguna bila modulnya mati:
  // tautannya mengarah ke route yang tidak ada di navigasi tenant ini.
  const insights: Insight[] = [
    ...(adaSocial ? [spikeInsight(days, (day) => `/a/social/posts?start=${day}&end=${day}`)] : []),
    negShareInsight({ positive: mt.positive + st.positive, neutral: mt.neutral + st.neutral, negative: mt.negative + st.negative },
                    { positive: pmt.positive + pst.positive, neutral: pmt.neutral + pst.neutral, negative: pmt.negative + pst.negative }),
    topMoverInsight(cats, (ref) => `/a/monitoring/articles?category=${encodeURIComponent(ref)}&sentiment=negative&${qs}`),
    ...(adaSocial ? [channelInsight(chans, (c) => `/a/social/posts?sourceType=${c}&${qs}`)] : []),
  ].filter(Boolean).slice(0, 4) as Insight[];

  return (
    <div className="avio-rise">
      <div style={{ display: "flex", alignItems: "flex-end", gap: "var(--space-6)", flexWrap: "wrap" }}>
        <div>
          <h1 className="avio-page-title" style={{ margin: 0 }}>Dashboard</h1>
          <div className="avio-t-sm avio-muted">Ringkasan {start} → {end} (WIB)</div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <DateRange start={start} end={end} onApply={(s2, e2) => { setStart(s2); setEnd(e2); load(s2, e2); }} />
        </div>
      </div>
      <SyncBar />

      {/* ---------- KARTU STATISTIK ---------- */}
      <div className="avio-tiles">
        {loading && [0, 1, 2, 3].map((i) => <div key={i} className="avio-sk" style={{ height: 128, borderRadius: "var(--radius-md)" }} />)}
        {!loading && <>
          {adaMedia && (
            <Tile c={1} icon="fa-solid fa-newspaper" label="Artikel media massa" value={mTot} sub="artikel"
              deltaPct={delta(mTot, pmTot)} href={`/a/monitoring/articles?${qs}`} />
          )}
          {adaSocial && (
            <Tile c={3} icon="fa-solid fa-bullhorn" label="Mention social" value={sTot} sub="mention"
              deltaPct={delta(sTot, psTot)} spark={totalSeries} href={`/a/social/posts?${qs}`} />
          )}
          <Tile c={2} icon="fa-solid fa-circle-exclamation" label="Negatif gabungan" value={mt.negative + st.negative}
            sub={`${pct(mt.negative + st.negative, mTot + sTot).toFixed(1)}% dari total`} goodWhenUp={false}
            deltaPct={delta(mt.negative + st.negative, pmt.negative + pst.negative)} spark={negSeries}
            href={`/a/social/posts?sentiment=negative&${qs}`} />
          <Tile c={4} icon="fa-solid fa-triangle-exclamation" label="Kategori berisiko" value={alerts.length}
            sub={alerts.length ? "perlu perhatian" : "semua normal"} href={`/a/monitoring/articles?sentiment=negative&${qs}`} />
        </>}
      </div>

      {/* ---------- STRIP INSIGHT ---------- */}
      {insights.length > 0 && (
        <div className="avio-strip">
          {insights.map((s) => {
            const inner = <><span className="avio-ins__ico"><i className={s.icon} /></span>{s.text}</>;
            return s.href
              ? <Link key={s.id} to={s.href} className="avio-ins" data-tone={s.tone}>{inner}</Link>
              : <span key={s.id} className="avio-ins" data-tone={s.tone}>{inner}</span>;
          })}
        </div>
      )}

      <div className="avio-grid">
        {/* ---------- GRAFIK UTAMA ---------- */}
        {adaSocial && <div data-col="8">
          <Panel icon="fa-solid fa-chart-area" title="Pergerakan percakapan" hint="social · harian (WIB)"
            action={
              <span className="avio-tabs" role="tablist">
                <button role="tab" aria-selected={mode === "tren"} onClick={() => setMode("tren")}>Tren</button>
                <button role="tab" aria-selected={mode === "tone"} onClick={() => setMode("tone")}>Per tone</button>
              </span>
            }>
            {mode === "tren" ? (
              <>
                <div className="avio-leg" style={{ marginBottom: "var(--space-5)" }}>
                  <span className="avio-leg__i"><span className="avio-leg__sw" style={{ background: BLUE }} />Total <b>{nf(sTot)}</b></span>
                  <span className="avio-leg__i"><span className="avio-leg__sw" style={{ background: M.neg }} />Negatif <b>{nf(st.negative)}</b></span>
                </div>
                <AreaChart labels={days.map((x) => wibDay(x.day))} series={[
                  { key: "total", label: "Total", color: BLUE, data: totalSeries },
                  { key: "neg", label: "Negatif", color: M.neg, data: negSeries },
                ]} />
              </>
            ) : (
              <>
                <div className="avio-leg" style={{ marginBottom: "var(--space-5)" }}>
                  {[["Positif", M.pos], ["Netral", M.neu], ["Negatif", M.neg]].map(([l, c]) => (
                    <span key={l} className="avio-leg__i"><span className="avio-leg__sw" style={{ background: c }} />{l}</span>
                  ))}
                </div>
                <DayCols data={days} height={228} hrefOf={(x) => `/a/social/posts?start=${x.day}&end=${x.day}`} />
              </>
            )}
          </Panel>
        </div>}

        {/* ---------- GAUGE TONE ---------- */}
        <div data-col="4">
          <Panel icon="fa-solid fa-gauge-high" title="Komposisi tone" hint="media massa">
            <div className="avio-gauges">
              <Gauge value={mt.positive} total={mTot} color={M.pos} label="Positif" />
              <Gauge value={mt.neutral} total={mTot} color={M.neu} label="Netral" />
              <Gauge value={mt.negative} total={mTot} color={M.neg} label="Negatif" />
            </div>
            {adaSocial && (
              <div style={{ marginTop: "var(--space-7)", paddingTop: "var(--space-5)", borderTop: "1px solid var(--color-border)" }}>
                <div className="avio-leg" style={{ justifyContent: "space-between" }}>
                  <span className="avio-leg__i">Social / SNA <b>{nf(sTot)}</b></span>
                  <Link to={`/a/social/posts?sentiment=negative&${qs}`} className="avio-leg__i" style={{ color: "var(--sent-neg)" }}>
                    negatif <b>{pct(st.negative, sTot).toFixed(1).replace(".", ",")}%</b>
                  </Link>
                </div>
              </div>
            )}
          </Panel>
        </div>

        {/* ---------- PROGRES ---------- */}
        {adaSocial && <div data-col="4">
          <Panel icon="fa-solid fa-mobile-screen" title="Kanal teratas" hint="social">
            {chans.length === 0
              ? <div className="avio-empty"><i className="fa-regular fa-circle-question avio-empty__icon" /><div className="avio-empty__title">Belum ada data kanal.</div></div>
              : <div className="avio-plist">
                  {chans.slice(0, 5).map((c) => (
                    <ProgressRow key={c.channel} name={<><Ch t={c.channel} /> {c.channel}</>}
                      href={`/a/social/posts?sourceType=${c.channel}&${qs}`} total={c.total} share={pct(c.total, chanSum)} maxShare={pct(chans[0]?.total ?? 0, chanSum)}
                      segs={[
                        { value: c.positive, color: M.pos, title: `positif ${c.positive}` },
                        { value: c.neutral, color: M.neu, title: `netral ${c.neutral}` },
                        { value: c.negative, color: M.neg, title: `negatif ${c.negative}` },
                      ]} />
                  ))}
                </div>}
          </Panel>
        </div>}
        <div data-col="4">
          <Panel icon="fa-solid fa-layer-group" title="Kategori" hint="media massa">
            {cats.length === 0
              ? <div className="avio-empty"><i className="fa-regular fa-circle-question avio-empty__icon" /><div className="avio-empty__title">Belum ada kategori.</div></div>
              : <div className="avio-plist">
                  {cats.slice(0, 5).map((c) => (
                    <ProgressRow key={c.ref} name={c.ref}
                      href={`/a/monitoring/articles?category=${encodeURIComponent(c.ref)}&${qs}`} total={c.total} share={pct(c.total, catSum)} maxShare={pct(cats[0]?.total ?? 0, catSum)}
                      segs={[
                        { value: c.positive, color: M.pos, title: `positif ${c.positive}` },
                        { value: c.neutral, color: M.neu, title: `netral ${c.neutral}` },
                        { value: c.negative, color: M.neg, title: `negatif ${c.negative}` },
                      ]} />
                  ))}
                </div>}
          </Panel>
        </div>
        <div data-col="4">
          <Panel icon="fa-solid fa-triangle-exclamation" title="Early Warning" hint="rule-based v1" flush>
            {alerts.length === 0 ? (
              <div className="avio-empty" style={{ padding: "var(--space-8)" }}>
                <i className="fa-regular fa-circle-check avio-empty__icon" style={{ color: "var(--sent-pos)" }} />
                <div className="avio-empty__title">Semua kategori normal</div>
              </div>
            ) : (
              <div className="avio-list">
                {alerts.slice(0, 5).map((e: any) => {
                  const t = EWS_TONE[e.status] ?? EWS_TONE.potential;
                  return (
                    <Link key={e.ref} className="avio-item" to={`/a/monitoring/articles?category=${encodeURIComponent(e.ref)}&sentiment=negative&${qs}`}>
                      <span className="avio-chip" data-shape="pill" style={{ background: t.bg, color: t.fg, minWidth: 74, justifyContent: "center" }}>{e.status}</span>
                      <span className="avio-item__main"><span className="avio-item__title">{e.ref}</span></span>
                      <span className="avio-item__side">{e.negative}/{e.total}<br /><span className="avio-muted">{e.sharePct}%</span></span>
                    </Link>
                  );
                })}
              </div>
            )}
          </Panel>
        </div>

        {/* ---------- TABEL BERDAMPAK ---------- */}
        <div data-col="12">
          <Panel icon="fa-solid fa-fire" title="Pemberitaan berdampak" hint="peringkat |News Value|" flush
            action={<Link to={`/a/monitoring/articles?${qs}`}>Semua artikel <i className="fa-solid fa-arrow-right" /></Link>}>
            {trending.length === 0
              ? <div className="avio-empty"><i className="fa-regular fa-newspaper avio-empty__icon" /><div className="avio-empty__title">Belum ada artikel.</div></div>
              : <div style={{ overflowX: "auto" }}>
                  <table className="avio-tbl">
                    <thead>
                      <tr>
                        <th style={{ width: 42 }}>#</th>
                        <th>Judul</th>
                        <th style={{ width: 150 }}>Media</th>
                        <th style={{ width: 110 }}>Tone</th>
                        <th style={{ width: 150 }} className="num">News Value</th>
                        <th style={{ width: 120 }} className="num">Tanggal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trending.slice(0, 6).map((x: any, i: number) => (
                        <tr key={x.id} style={{ cursor: "pointer" }} onClick={() => setDetailId(x.id)}>
                          <td className="avio-sub">{i + 1}</td>
                          <td><div className="avio-cellwrap"><b><Highlight text={x.title} /></b></div></td>
                          <td className="avio-sub">{x.media ?? "—"}</td>
                          <td>{x.sentimentLabel && <span className="avio-chip" data-sentiment={x.sentimentLabel}><span className="avio-chip__label">{TONE_ID[x.sentimentLabel]}</span></span>}</td>
                          <td className="num">{x.newsValue != null ? `Rp ${nf(Math.abs(x.newsValue))}` : "—"}</td>
                          <td className="num avio-sub">{new Date(new Date(x.publishedAt).getTime() + 7 * 3600e3).toISOString().slice(5, 10).replace("-", "/")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>}
          </Panel>
        </div>

        {/* ---------- ORANG ---------- */}
        <div data-col="6">
          <Panel icon="fa-solid fa-user-tie" title="Tokoh paling dikutip" hint="media massa" flush
            action={<Link to="/a/analysis/persons">Semua <i className="fa-solid fa-arrow-right" /></Link>}>
            <div className="avio-list">
              {persons.map((p: any, i: number) => (
                <Link key={p.person} className="avio-item" to={`/a/monitoring/articles?person=${encodeURIComponent(p.person)}&${qs}`}>
                  <span className="avio-item__rank">{i + 1}</span>
                  <span className="avio-ava">{initials(p.person)}</span>
                  <span className="avio-item__main">
                    <span className="avio-item__title">{p.person}</span>
                    <span className="avio-item__meta">
                      <span style={{ color: "var(--sent-pos)" }}>{p.tones?.positive ?? 0} positif</span>
                      <span style={{ color: "var(--sent-neg)" }}>{p.tones?.negative ?? 0} negatif</span>
                    </span>
                  </span>
                  <span className="avio-item__side">{nf(p.mentions)} artikel</span>
                </Link>
              ))}
              {persons.length === 0 && <div className="avio-empty"><i className="fa-regular fa-user avio-empty__icon" /><div className="avio-empty__title">Belum ada tokoh terdeteksi.</div></div>}
            </div>
          </Panel>
        </div>
        {adaSocial && <div data-col="6">
          <Panel icon="fa-solid fa-star" title="Influencer teratas" hint="by engagement" flush
            action={<Link to="/a/social/influencers">Semua <i className="fa-solid fa-arrow-right" /></Link>}>
            <div className="avio-list">
              {authors.map((a: any, i: number) => (
                <Link key={a.authorId} className="avio-item" to={`/a/social/posts?author=${encodeURIComponent(a.handle)}&${qs}`}>
                  <span className="avio-item__rank">{i + 1}</span>
                  <span className="avio-ava">{(a.handle ?? "?").slice(0, 2).toUpperCase()}</span>
                  <span className="avio-item__main">
                    <span className="avio-item__title">@{a.handle}</span>
                    <span className="avio-item__meta">{nf(a.followers)} pengikut · {a.posts} post</span>
                  </span>
                  <span className="avio-item__side"><i className="fa-solid fa-thumbs-up" /> {nf(a.likes)}</span>
                </Link>
              ))}
              {authors.length === 0 && <div className="avio-empty"><i className="fa-regular fa-star avio-empty__icon" /><div className="avio-empty__title">Belum ada influencer.</div></div>}
            </div>
          </Panel>
        </div>}

        {/* ---------- ISSUE + NETWORK ---------- */}
        {adaIssue && <div data-col="5">
          <Panel icon="fa-solid fa-fire-extinguisher" title="Issue aktif" flush
            action={<Link to="/a/issues">Kelola <i className="fa-solid fa-arrow-right" /></Link>}>
            <div className="avio-list">
              {issues.map((x: any) => (
                <Link key={x.id} className="avio-item" to={`/a/issues/${x.id}`}>
                  <span className="avio-chip" data-shape="pill"
                    style={{ background: x.severity === "critical" || x.severity === "viral" ? "var(--status-danger-bg)" : "var(--status-warn-bg)",
                             color: x.severity === "critical" || x.severity === "viral" ? "var(--status-danger)" : "var(--status-warn)", minWidth: 66, justifyContent: "center" }}>
                    {x.severity}
                  </span>
                  <span className="avio-item__main">
                    <span className="avio-item__title">{x.title}</span>
                    <span className="avio-item__meta">{x.stats.total} bukti · {x.stats.negative} negatif · {x.status}</span>
                  </span>
                </Link>
              ))}
              {issues.length === 0 && <div className="avio-empty"><i className="fa-regular fa-circle-check avio-empty__icon" style={{ color: "var(--sent-pos)" }} /><div className="avio-empty__title">Tidak ada issue aktif.</div></div>}
            </div>
          </Panel>
        </div>}
        {adaSocial && has("topic_network") && <div data-col="7">
          <Panel icon="fa-solid fa-circle-nodes" title="Topic network" hint="SNA · klik bubble untuk pendalaman"
            action={<Link to="/a/social/analytics">Perbesar <i className="fa-solid fa-arrow-right" /></Link>}>
            {net?.nodes?.length
              ? <NetworkGraph nodes={(net.nodes ?? []).slice(0, 14)}
                  edges={(net.edges ?? []).filter((e: any) => (net.nodes ?? []).slice(0, 14).some((n: any) => n.id === e.x) && (net.nodes ?? []).slice(0, 14).some((n: any) => n.id === e.y))}
                  height={320} hrefOf={() => "/a/social/analytics"} onNode={undefined as any} />
              : <div className="avio-empty"><i className="fa-solid fa-circle-nodes avio-empty__icon" /><div className="avio-empty__title">Graf belum terbentuk.</div></div>}
          </Panel>
        </div>}
      </div>
      {detailId && <Drawer id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}
